import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import SoftAvatar from "components/SoftAvatar";
import SoftBadge from "components/SoftBadge";
import defaultAvatar from "assets/images/team-2.jpg";
import api from "api/api";
import { ApiResponse } from "model/apiResponseModel";
import { UserModel } from "model/userResponseModel";
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import SearchField from "components/SearchField";
import SoftButton from "components/SoftButton";
import userImage from "assets/images/illustrations/user.png";

function UsersList() {
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 10; // Number of users per page
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearch = () => {
    if (searchQuery != "") {
      fetchUsers(1, searchQuery);
    }
  };

  const handleClearSearch = () => {
    if (searchQuery != "") {
      setSearchQuery("");
      fetchUsers(1, "");
    }
  };

  const fetchUsers = async (page = 1, search = "") => {
    try {
      setIsLoading(true);
      const postData = { limit, page };

      if (search && search.trim() !== "") {
        postData.search = search.trim();
      }
      const response = await api.getUsersList(postData);
      const apiResponse = new ApiResponse(response.status, response.message);

      if (apiResponse.status === 1) {
        const userModelData = new UserModel(response.data);
        setColumns([
          { name: "user", align: "left" },
          // { name: "type", align: "center" },
          { name: "projects", align: "center" },
          { name: "joined", align: "center" },
          { name: "action", align: "center" },
          { name: "status", align: "center" },
        ]);
        setRows(
          userModelData.users.map((user) => ({
            linkPath: `/user-detail?userId=${user._id}`,
            user: (
              <SoftBox display="flex" alignItems="center" px={1} pb={0.5}>
                <SoftAvatar src={user.profile_picture || userImage} alt={user.name} size="sm" variant="rounded" />
                <SoftBox display="flex" flexDirection="column" ml={2}>
                  <SoftTypography variant="button" fontWeight="medium">
                    {user.name}
                  </SoftTypography>
                  <SoftTypography variant="caption" color="secondary">
                    {user.email}
                  </SoftTypography>
                </SoftBox>
              </SoftBox>
            ),
            projects: (
              <SoftTypography
                component={Link}
                variant="caption"
                to={`/projects?userId=${user._id}`}
                color="secondary" fontWeight="medium"
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%',
                  minHeight: '40px'
                }}
                >
                {user.projectCount.toString()}
              </SoftTypography>
            ),
            // type: (
            //   <SoftBadge
            //     variant="gradient"
            //     badgeContent={user.isPremiumUser === "true" ? "premium" : "basic"}
            //     color={user.isPremiumUser === "true" ? "success" : "error"}
            //     size="xs"
            //   />
            // ),
            status: (
              <SoftBadge
                variant="gradient"
                badgeContent={user.is_deleted ? "deleted" : "active"}
                color={user.is_deleted ? "error" : "success"}
                size="xs"
              />
            ),
            joined: (
              <SoftTypography variant="caption" color="secondary" fontWeight="medium"
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                minHeight: '40px'
              }}
              >
                {new Date(user.created_at).toLocaleDateString("en-US", {
                  month: "2-digit",
                  day: "2-digit",
                  year: "numeric",
                })}
              </SoftTypography>
            ),
            action: (
              <SoftTypography
                component={Link}
                to={`/user-detail?userId=${user._id}`}
                variant="caption"
                color="secondary"
                fontWeight="medium"
                sx={{ 
                  display: 'flex',
                  alignItems: 'center',
                  height: '100%',
                  minHeight: '40px'
                }}
              >
                Edit
              </SoftTypography>
            ),
          }))
        );
        setTotalPages(response.totalPages);
      } else {
        alert(apiResponse.message);
      }
    } catch (error) {
      console.error("Error fetching users:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const navigate = useNavigate();

  useEffect(() => {
    const loginData = JSON.parse(localStorage.getItem("loginData") || sessionStorage.getItem("loginData"));

    // Check if the user is logged in
    if (!loginData) {
      navigate('/authentication/sign-in');
    } else {
      // If logged in, fetch users for the current page
      fetchUsers(currentPage);
    }
  }, [currentPage, navigate]);

  const handlePageClick = (page) => {

    // if (page >= 1 && page <= totalPages) {
    setCurrentPage(page);
    // console.log(page);
    fetchUsers(page);
    // }
  };

  // const handlePageChange = (event, page) => {
  //   setCurrentPage(page);
  //   fetchUsers(currentPage);
  // };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Card>
            <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
              <SoftTypography variant="h6">Users Table</SoftTypography>
              <div>
                <SearchField
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onSearch={handleSearch}
                  onClear={handleClearSearch}
                />
              </div>
            </SoftBox>
            <SoftBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {isLoading ? (
                <SoftBox display="flex" height="500px" justifyContent="center" alignItems="center" p={3}>
                  <SoftTypography variant="subtitle1" color="textSecondary">
                    Loading...
                  </SoftTypography>
                </SoftBox>
              ) : rows.length > 0 ? (
                <Table columns={columns} rows={rows} />
              ) : (
                <SoftBox display="flex" justifyContent="center" alignItems="center" p={3}>
                  <SoftTypography variant="subtitle1" color="textSecondary">
                    No Users Available
                  </SoftTypography>
                </SoftBox>
              )}
            </SoftBox>
            <SoftBox display="flex" alignItems="center" justifyContent="center" py={2}>
              {/* Previous Button */}
              <SoftButton
                variant="outlined"
                color="primary"
                onClick={() => handlePageClick(currentPage - 1)}
                disabled={currentPage === 1}
                sx={{ mx: 1 }}
              >
                Previous
              </SoftButton>

              {/* Page Numbers */}
              {/* {[...Array(totalPages)].map((_, index) => {
        const pageNumber = index + 1;
        return ( */}
              <SoftButton
                key={currentPage}
                variant="contained"
                color="primary"
                // onClick={() => handlePageClick(pageNumber)}
                sx={{ mx: 0.5 }}
              >
                {currentPage}
              </SoftButton>
              {/* );
      })} */}

              {/* Next Button */}
              <SoftButton
                variant="outlined"
                color="primary"
                onClick={() => handlePageClick(currentPage + 1)}
                disabled={currentPage === totalPages}
                sx={{ mx: 1 }}
              >
                Next
              </SoftButton>
            </SoftBox>
          </Card>
        </SoftBox>
      </SoftBox>
      <Footer />
    </DashboardLayout>
  );
}

export default UsersList;
